import { useMemo } from "react";
import useSWR from "swr";
import { AgentAvatarInput } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { Agent, fetchAgentsByIds } from "~/services/agents";
import { FeaturedSession } from "~/services/projects";
import { getSharedConversation } from "~/services/shares";

// SWR key prefix for a featured entry's live share read. Exported so the run-end
// revalidation (`useProjectSessionsSync`) can target these reads by prefix
// without duplicating the literal — keeping the two in lockstep.
export const FEATURED_SESSION_SHARE_KEY = "featured-session-share";

// A featured session references its agent by id only (see `FeaturedSession`),
// so the rail must resolve it itself. Most rows resolve for free against the
// project's already-loaded agents; only ids missing from that set (e.g. an
// agent later detached from the project) trigger the batch lookup, and a caller
// who can't view that agent gets `undefined` back — rendered without an avatar.
export const useFeaturedSessionAgents = (
  featuredSessions: FeaturedSession[] | undefined,
  attachedAgentsWithDetails: Agent[] | undefined
): Map<string, Agent> => {
  const attachedById = useMemo(
    () => new Map((attachedAgentsWithDetails ?? []).map((a) => [a.id, a])),
    [attachedAgentsWithDetails]
  );

  const missingAgentIds = useMemo(
    () =>
      [
        ...new Set(
          (featuredSessions ?? [])
            .map((f) => f.agent_id)
            .filter((id): id is string => id != null && !attachedById.has(id))
        ),
      ].sort(),
    [featuredSessions, attachedById]
  );

  const { data: fetchedAgents } = useSWR<Agent[]>(
    missingAgentIds.length ? ["featured-session-agents", ...missingAgentIds] : null,
    ([, ...ids]: [string, ...string[]]) => fetchAgentsByIds(ids)
  );

  return useMemo(() => {
    const map = new Map(attachedById);
    for (const agent of fetchedAgents ?? []) {
      map.set(agent.id, agent);
    }
    return map;
  }, [attachedById, fetchedAgents]);
};

// A featured entry's served set is resolved live from its share — the
// serving-agent summaries on the shared-conversation read — so an agent that
// joined the session after it was pinned still appears. SWR's own cache dedupes
// entries that happen to share a token. Only when the share can't be read
// (revoked, or not permitted to the viewer) does the row fall back to the
// entry's stored `agent_id`, already resolved against the roster by the caller.
export const useFeaturedSessionServedAgents = (
  shareToken: string,
  fallbackAgent: Agent | undefined
): AgentAvatarInput[] => {
  const { data: share } = useSWR([FEATURED_SESSION_SHARE_KEY, shareToken], () =>
    getSharedConversation(shareToken)
  );

  return useMemo(() => {
    if (share) {
      return share.agents;
    }
    return fallbackAgent ? [fallbackAgent] : [];
  }, [share, fallbackAgent]);
};
